'use client';

import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import useTouchDevice from 'helpers/hooks/useTouchDevice';
import { getScrollbarWidth } from 'helpers/utils/getScrollbarWidth';

type BlockScrollContext = {
  blocked: number;
  blockScroll: () => void;
};

const Context = createContext<BlockScrollContext | null>(null);

const BlockScrollProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [blocked, setBlocked] = useState(0);
  const { isTouchDevice } = useTouchDevice();

  useEffect(() => {
    if (blocked > 0) {
      const scrollbarWidth = getScrollbarWidth();
      if (!isTouchDevice) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }

      document.body.style.overflowY = 'hidden';
    } else {
      document.body.style.overflowY = 'auto';
      if (!isTouchDevice) {
        document.body.style.paddingRight = '0px';
      }
    }
  }, [blocked, isTouchDevice]);

  const blockScroll = useCallback(() => {
    setBlocked((b) => b + 1);
    return () => {
      setBlocked((b) => b - 1);
    };
  }, []);

  return <Context.Provider value={{ blocked, blockScroll }}>{children}</Context.Provider>;
};

export const useScrollBlock = () => {
  const context = useContext(Context);
  if (!context) throw new Error('Missing BlockScrollContext');
  return { blockScroll: context.blockScroll };
};

export default BlockScrollProvider;
